import React, { Suspense, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useScroll, useMotionValueEvent } from "framer-motion";

import { PageSeo } from "@/components/seo/PageSeo";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { MaskReveal } from "@/components/motion/MaskReveal";
import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { usePrefillArea } from "@/hooks/use-contact-prefill";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

const SceneF = React.lazy(() => import("@/components/scenes/SceneF"));

const DECARB_URL = "https://decarb.frac.io/";

function ExternalPrimary({ label, ariaLabel, testId, size = "md" }) {
  return (
    <Button
      as="a"
      href={DECARB_URL}
      target="_blank"
      rel="noopener noreferrer"
      variant="primary-on-dark"
      size={size}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {label}
      <ArrowUpRight className="ml-1 h-4 w-4" strokeWidth={2} aria-hidden />
    </Button>
  );
}

function useMotionValueState(mv, initial = 0) {
  const [value, setValue] = useState(initial);
  useMotionValueEvent(mv, "change", (v) => setValue(v));
  return value;
}

function SceneFallback() {
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, background: "var(--surface-base)" }} />
  );
}

function Hero() {
  const { t } = useT("platformEnergy");
  const ctaLabel = t("cta.label");
  const ctaAria = ctaLabel + t("cta.ariaSuffix");
  return (
    <section
      className="container-frac pt-24 pb-16 md:pt-32 md:pb-24"
      data-testid="energy-hero"
    >
      <div className="max-w-4xl">
        <Reveal>
          <div className="flex flex-wrap items-center gap-3">
            <span className="eyebrow" data-testid="page-eyebrow">
              {t("hero.eyebrow")}
            </span>
            <Badge status="ROADMAP" onDark data-testid="energy-hero-badge">{t("badgeRoadmap")}</Badge>
          </div>
        </Reveal>
        <MaskReveal
          as="h1"
          className="mt-6 text-4xl font-bold leading-tight tracking-tight text-ink-primary sm:text-5xl lg:text-6xl"
          data-testid="page-h1"
        >
          {t("hero.h1")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("hero.body")}
          </p>
        </Reveal>
        <Reveal delay={300}>
          <div className="mt-8 flex flex-col items-start gap-3">
            <Badge status="LIVE" onDark data-testid="energy-cta-hero-badge">
              {t("cta.liveBadge")}
            </Badge>
            <ExternalPrimary
              label={ctaLabel}
              ariaLabel={ctaAria}
              testId="energy-cta-hero"
              size="lg"
            />
          </div>
        </Reveal>
        <Reveal delay={420}>
          <p className="mt-6 text-sm italic text-ink-tertiary">
            {t("hero.caveat")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function GridScene() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const progress = useMotionValueState(scrollYProgress, 0);

  return (
    <section
      ref={ref}
      className="relative"
      data-testid="energy-grid"
    >
      <div className="relative min-h-[180vh]">
        <div className="sticky top-0 flex min-h-screen items-center py-16">
          <div className="container-frac w-full">
            <div
              className="relative h-[65vh] w-full overflow-hidden rounded-[16px] border border-hairline"
              data-testid="energy-scene-f-wrapper"
            >
              <Suspense fallback={<SceneFallback />}>
                <SceneF
                  scrollProgress={progress}
                  style={{ position: "absolute", inset: 0 }}
                />
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FourThings() {
  const { t } = useT("platformEnergy");
  const items = t("fourThings.items", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="energy-things"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("fourThings.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("fourThings.h2")}
        </MaskReveal>
      </div>

      <Stagger className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2" gap={70}>
        {items.map((tt, i) => (
          <StaggerItem key={i}>
            <Card variant="glass" className="h-full" data-testid={`energy-thing-${i + 1}`}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-base font-bold text-ink-primary">
                  {tt.name}
                </h3>
                <Badge status="ROADMAP" onDark>{t("badgeRoadmap")}</Badge>
              </div>
              <p className="mt-3 text-sm leading-normal text-ink-secondary">
                {tt.body}
              </p>
            </Card>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function Thesis() {
  const { t } = useT("platformEnergy");
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="energy-thesis"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("thesis.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("thesis.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("thesis.body")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Boundaries() {
  const { t } = useT("platformEnergy");
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="energy-boundaries"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("boundaries.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("boundaries.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("boundaries.body")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  const { t } = useT("platformEnergy");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="energy-final-cta"
    >
      <div className="mx-auto max-w-3xl text-center">
        <MaskReveal
          as="h2"
          className="text-3xl font-bold leading-tight tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("finalCta.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-6 text-lg leading-normal text-ink-secondary">
            {t("finalCta.body")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/contact", locale)}
              variant="primary-on-dark"
              size="lg"
              data-testid="energy-final-cta-primary"
            >
              {t("finalCta.cta")}
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function BottomCta() {
  const { t } = useT("platformEnergy");
  const ctaLabel = t("cta.label");
  const ctaAria = ctaLabel + t("cta.ariaSuffix");
  return (
    <section
      className="container-frac pb-24 md:pb-32"
      data-testid="energy-bottom-cta"
    >
      <div className="mx-auto max-w-3xl text-center">
        <Reveal>
          <span className="eyebrow" data-testid="bottom-cta-eyebrow">
            {t("bottomCta.eyebrow")}
          </span>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-4 text-lg leading-normal text-ink-secondary">
            {t("bottomCta.body")}
          </p>
        </Reveal>
        <Reveal delay={260}>
          <div className="mt-8 flex flex-col items-center gap-3">
            <Badge status="LIVE" onDark data-testid="energy-cta-bottom-badge">
              {t("cta.liveBadge")}
            </Badge>
            <ExternalPrimary
              label={ctaLabel}
              ariaLabel={ctaAria}
              testId="energy-cta-bottom"
              size="lg"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function PlatformEnergy() {
  usePrefillArea("energy_green_compute");

  return (
    <>
      <PageSeo
        title="Energy & Green Compute — Intelligence Has a Power Bill"
        description="Compute entitlements, green-compute reporting, and the tokenization of energy data itself. Direction of travel, subject to partnership and regulatory review."
        path="/platform/energy"
        image="/og/platform-energy.png"
      />
      <div data-testid="page-platform-energy">
        <Hero />
        <GridScene />
        <FourThings />
        <Thesis />
        <Boundaries />
        <BottomCta />
        <FinalCta />
      </div>
    </>
  );
}
